import { sortChartPoints } from "./utils";

export const ASSET_UNIFORMAT_LEVEL_CHART_TYPES = [
    "uniformat_level_1s",
    "uniformat_level_2s",
    "uniformat_level_3s",
    "uniformat_level_4s",
    "uniformat_level_5s",
    "uniformat_level_6s"
];

export const END_OF_LIFE_DEFAULT_GROUP_BY = "end_of_life_condition";
export const END_OF_LIFE_CONDITION_GROUP_BY = "asset_condition";

export const RANGE_CHART_GROUP_BY = {
    asset_end_of_life: { label: "End of Service Life", filterKey: "end_of_life_chart_range" },
    asset_age_chart: { label: "Age", filterKey: "assets_age_chart_range" }
};

export const isRangeFilterKey = filterKey => !!filterKey && Object.values(RANGE_CHART_GROUP_BY).some(option => option.filterKey === filterKey);

export const ASSET_AGE_CONDITION_GROUP_BY = "assets_age_chart";

export const GEO_HIERARCHY_GROUP_BY_OPTIONS = [
    { value: "project", label: "Project", filterKey: "projects.name" },
    { value: "region", label: "Region", filterKey: "regions.name" },
    { value: "site", label: "Site", filterKey: "sites.name" },
    { value: "building", label: "Building", filterKey: "buildings.name" },
    { value: "building_type", label: "Building Type", filterKey: "main_building_types.name" },
    { value: "addition", label: "Addition", filterKey: "additions.name" },
    { value: "floor", label: "Floor", filterKey: "floors.name" }
];

export const END_OF_LIFE_GROUP_BY_OPTIONS = [
    { value: END_OF_LIFE_DEFAULT_GROUP_BY, label: "End of Service Life by Condition" },
    { value: ASSET_AGE_CONDITION_GROUP_BY, label: "Age by Condition" },
    { value: END_OF_LIFE_CONDITION_GROUP_BY, label: "Condition" },
    ...Object.entries(RANGE_CHART_GROUP_BY).map(([value, { label }]) => ({ value, label })),
    ...GEO_HIERARCHY_GROUP_BY_OPTIONS.map(({ value, label }) => ({ value, label })),
    ...ASSET_UNIFORMAT_LEVEL_CHART_TYPES.map((value, index) => ({ value, label: `Uniformat Level ${index + 1}` }))
];

export const SLOT_ONE_CHART_OPTIONS = [
    { value: "End_Of_Life_By_Year", paramKey: "end_of_life_chart", label: "End of Service Life by Condition (Asset Count)" },
    { value: "Asset_Age_By_Condition", paramKey: "assets_age_chart", label: "Age by Condition" }
];
export const SLOT_ONE_CHART_KEY = "End_Of_Life_By_Year";

const VALUE_MEASURE_SUFFIX = "__value";

export const supportsMeasureToggle = optionValue => !!optionValue;

export const isCostMeasureOption = optionValue => !!optionValue && `${optionValue}`.endsWith(VALUE_MEASURE_SUFFIX);

export const getMeasureOption = (optionValue, measure) => {
    const countValue = `${optionValue || ""}`.replace(VALUE_MEASURE_SUFFIX, "");
    if (!countValue) return null;
    return measure === "cost" ? `${countValue}${VALUE_MEASURE_SUFFIX}` : countValue;
};

export const getCountChartType = optionValue => getMeasureOption(optionValue, "count");

const API_CHART_TYPE_BY_OPTION = {
    [END_OF_LIFE_DEFAULT_GROUP_BY]: "end_of_life_chart"
};

export const getApiChartType = optionValue => {
    const countType = getCountChartType(optionValue) || END_OF_LIFE_DEFAULT_GROUP_BY;
    return API_CHART_TYPE_BY_OPTION[countType] || countType;
};

export const isGroupedCostChart = groupBy => isCostMeasureOption(groupBy);

export const isRangeChartGroupBy = groupBy => !!RANGE_CHART_GROUP_BY[getCountChartType(groupBy)];

const GROUP_BY_DISPLAY_ORDER = [END_OF_LIFE_DEFAULT_GROUP_BY, "asset_end_of_life", "asset_age_chart", ASSET_AGE_CONDITION_GROUP_BY];

export const sortGroupByOptions = (options = []) =>
    [...options].sort((a, b) => {
        const first = GROUP_BY_DISPLAY_ORDER.indexOf(a.value);
        const second = GROUP_BY_DISPLAY_ORDER.indexOf(b.value);
        if (first === second) return 0;
        if (first === -1) return 1;
        if (second === -1) return -1;
        return first - second;
    });

export const getGroupByOptions = () => sortGroupByOptions(END_OF_LIFE_GROUP_BY_OPTIONS.filter(option => !isCostMeasureOption(option.value)));

export const getSelectedGroupByOption = groupBy => {
    const options = getGroupByOptions();
    const baseValue = getCountChartType(groupBy) || END_OF_LIFE_DEFAULT_GROUP_BY;
    return options.find(option => option.value === baseValue) || options[0];
};

export const isSlotOneCostChart = (type, endOfLifeGroupBy) => type === SLOT_ONE_CHART_KEY && isGroupedCostChart(endOfLifeGroupBy);

export const isAgeConditionGroupBy = groupBy => getCountChartType(groupBy) === ASSET_AGE_CONDITION_GROUP_BY;

export const isEndOfLifeGrouped = groupBy =>
    !!groupBy && ![END_OF_LIFE_DEFAULT_GROUP_BY, ASSET_AGE_CONDITION_GROUP_BY].includes(getCountChartType(groupBy));

export const getEndOfLifeGroupByLabel = groupBy =>
    END_OF_LIFE_GROUP_BY_OPTIONS.find(option => option.value === getCountChartType(groupBy))?.label || "";

export const getEndOfLifeChartHeading = groupBy => {
    const label = getEndOfLifeGroupByLabel(groupBy) || END_OF_LIFE_GROUP_BY_OPTIONS[0].label;
    if (isGroupedCostChart(groupBy)) return `${label} (Asset Cost)`;
    return isEndOfLifeGrouped(groupBy) || isAgeConditionGroupBy(groupBy) ? label : `${label} (Asset Count)`;
};

export const getGroupedChartParams = (chartParams = {}, scopeParams = {}, chartType) => ({
    ...scopeParams,
    chart_type: chartType || getApiChartType(chartParams?.end_of_life_group_by),
    chart_sort_by: "value",
    list: chartParams?.list || {},
    filters: chartParams?.filters || null,
    sfci_chart_sort_by: chartParams?.sfci_chart_sort_by || "value",
    sfci_chart_sort_order: chartParams?.sfci_chart_sort_order || "desc",
    user_id: localStorage.getItem("userId")
});

const GROUP_BY_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];

export const getGroupColor = index => GROUP_BY_COLORS[index] || `hsl(${(index * 137.508) % 360}, 62%, 45%)`;

const toCount = value => {
    const count = Number(value);
    return Number.isFinite(count) ? count : 0;
};

const GROUPED_ROW_KEYS = ["chart_data", "chart", "charts", "data", "results", "result", "end_of_life_chart"];

const findGroupedRows = payload => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return [];
    const namedRows = GROUPED_ROW_KEYS.map(key => payload[key]).find(value => Array.isArray(value));
    if (namedRows) return namedRows;
    return Object.values(payload).find(value => Array.isArray(value) && (!value.length || typeof value[0] === "object")) || [];
};

const getGroupedRowCount = row => toCount(row?.asset_count ?? row?.count ?? row?.values);

const getGroupedRowValue = row => toCount(row?.value ?? row?.cost ?? row?.money);

const getGroupedRowValueCount = row => toCount(row?.value_asset_count ?? row?.asset_count ?? row?.count);

const CONDITION_CHART_TYPES = ["end_of_life_chart", "assets_age_chart"];

export const isConditionChartType = chartType => CONDITION_CHART_TYPES.includes(chartType);

export const getConditionRangeNames = (groups = []) => {
    const seen = new Map();
    let widest = 0;
    groups.forEach(group => {
        const data = Array.isArray(group?.data) ? group.data : [];
        widest = Math.max(widest, data.length);
        data.forEach(item => {
            const range = item?.range;
            if (range == null || range === "") return;
            const key = `${range}`.toLowerCase();
            if (!seen.has(key)) seen.set(key, `${range}`);
        });
    });
    if (!seen.size && widest) return Array.from({ length: widest }, (unused, index) => `${index + 1}`);
    return [...seen.values()];
};

export const getConditionRangeFilterKey = groupBy =>
    isAgeConditionGroupBy(groupBy) ? RANGE_CHART_GROUP_BY.asset_age_chart.filterKey : RANGE_CHART_GROUP_BY.asset_end_of_life.filterKey;

export const getConditionRangeLabel = groupBy => (isAgeConditionGroupBy(groupBy) ? "Age" : "Useful Life Remaining");

const normalizeConditionChartRows = payload => {
    const groups = Array.isArray(payload?.["end_of_life_chart_by_group"]) ? payload["end_of_life_chart_by_group"] : [];
    const ranges = getConditionRangeNames(groups);
    return groups.map((group, index) => {
        const condition = group?.condition || group?.condtion || {};
        const data = Array.isArray(group?.data) ? group.data : [];
        const color = condition.color || condition.colour || group?.color || group?.colour || getGroupColor(index);
        return {
            condtion: { id: condition.id ?? group?.id ?? null, name: condition.name || group?.name || "Unknown", color },
            // Zero-filled onto a shared axis: ChartDataPopUp indexes data[] by category position.
            data: ranges.map((range, rangeIndex) => {
                const item = data.find(entry => `${entry?.range ?? ""}`.toLowerCase() === range.toLowerCase()) || data[rangeIndex] || {};
                return {
                    range,
                    values: getGroupedRowCount(item),
                    value: getGroupedRowValue(item),
                    assetCount: getGroupedRowCount(item),
                    valueAssetCount: getGroupedRowValueCount(item),
                    color: item.color || item.colour || color
                };
            })
        };
    });
};

export const LEGEND_DEFAULT_SORT = { sortBy: "name", sortAsc: true };

export const getLegendSort = (sortBy, sortAsc) => (sortBy ? { sortBy, sortAsc: !!sortAsc } : LEGEND_DEFAULT_SORT);

export const SLOT_ONE_DEFAULT_SORT = LEGEND_DEFAULT_SORT;

export const getSlotOneSort = (chartParams = {}) => ({
    sortBy: chartParams?.slot_one_chart_sort_by || SLOT_ONE_DEFAULT_SORT.sortBy,
    sortAsc: chartParams?.slot_one_chart_sort_asc ?? SLOT_ONE_DEFAULT_SORT.sortAsc
});

export const isSlotOneSortChanged = (chartParams = {}) => {
    const { sortBy, sortAsc } = getSlotOneSort(chartParams);
    return sortBy !== SLOT_ONE_DEFAULT_SORT.sortBy || sortAsc !== SLOT_ONE_DEFAULT_SORT.sortAsc;
};

const sortConditionRows = (rows, sort) => {
    const { sortBy, sortAsc } = sort || SLOT_ONE_DEFAULT_SORT;
    if (sortBy !== "name" && sortBy !== "value") return rows || [];
    const sorted = sortChartPoints(
        (rows || []).map((row, index) => ({
            index,
            name: row?.condtion?.name || row?.condition?.name || "",
            y: (row?.data || []).reduce((total, range) => total + (Number(range?.values) || 0), 0)
        })),
        sortBy,
        sortAsc
    );
    return sorted.map(item => rows[item.index]);
};

export const getConditionChartRows = (rows, groupBy, sort) => {
    const measuredRows = !isGroupedCostChart(groupBy)
        ? rows || []
        : (rows || []).map(row => ({
              ...row,
              data: (row?.data || []).map(range => ({
                  ...range,
                  values: range.value || 0,
                  assetCount: range.valueAssetCount || 0,
                  totalAssetCount: range.assetCount || 0
              }))
          }));
    return sortConditionRows(measuredRows, sort);
};

export const FCA_COST_TYPES = [
    { value: "with_cost", label: "With Cost" },
    { value: "without_cost", label: "Without Cost" },
    { value: "all", label: "All" }
];

export const normalizeGroupedChartRows = (payload, chartType) => {
    if (isConditionChartType(chartType)) return normalizeConditionChartRows(payload);
    const rows = findGroupedRows(payload);
    return rows.map((row, index) => ({
        name: row?.name || row?.label || row?.group_name || row?.condition?.name || row?.condtion?.name || row?.range || "Unknown",
        y: getGroupedRowCount(row),
        value: getGroupedRowValue(row),
        color: row?.color || row?.colour || row?.condition?.color || row?.condtion?.color || getGroupColor(index),
        groupId: row?.id ?? row?.condition?.id ?? row?.condtion?.id ?? null,
        assetCount: getGroupedRowCount(row),
        valueAssetCount: getGroupedRowValueCount(row)
    }));
};

export const getGroupedAssetFilterKey = groupBy => {
    if (!isEndOfLifeGrouped(groupBy)) return null;
    const countGroupBy = getCountChartType(groupBy);
    if (countGroupBy === END_OF_LIFE_CONDITION_GROUP_BY) return "client_asset_conditions.name";
    if (isRangeChartGroupBy(countGroupBy)) return RANGE_CHART_GROUP_BY[countGroupBy].filterKey;
    const geoOption = GEO_HIERARCHY_GROUP_BY_OPTIONS.find(option => option.value === countGroupBy);
    if (geoOption) return geoOption?.filterKey;
    return ASSET_UNIFORMAT_LEVEL_CHART_TYPES.includes(countGroupBy) ? `${countGroupBy}.name` : null;
};

export const getGroupedChartPopupData = ({ heading, groupBy, chartView, point }) => ({
    heading,
    category: "",
    name: point?.name || "",
    entityId: point?.groupId ?? null,
    data: point?.y ?? 0,
    assetCount: point?.assetCount ?? 0,
    totalAssetCount: point?.totalAssetCount ?? 0,
    type: "End_Of_Life_By_Year",
    chartView,
    groupBy,
    groupLabel: getEndOfLifeGroupByLabel(groupBy),
    groupFilterKey: getGroupedAssetFilterKey(groupBy),
    groupIsCost: isGroupedCostChart(groupBy)
});

export const AXIS_LABEL_TOOLTIP_ID = "asset-chart-axis-label";

const escapeHtml = value =>
    `${value ?? ""}`.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const AXIS_LABEL_CHAR_WIDTH = 7;
const AXIS_LABEL_GUTTER = 10;
const AXIS_LABEL_MIN_CHARS = 4;

export const getAxisLabelMaxChars = labelContext => {
    const axisLength = labelContext?.axis?.len || labelContext?.chart?.plotWidth || 0;
    const categoryCount = labelContext?.axis?.tickPositions?.length || labelContext?.axis?.categories?.length || 0;
    if (!axisLength || !categoryCount) return Number.MAX_SAFE_INTEGER;
    return Math.max(AXIS_LABEL_MIN_CHARS, Math.floor((axisLength / categoryCount - AXIS_LABEL_GUTTER) / AXIS_LABEL_CHAR_WIDTH));
};

export const buildAxisLabelHtml = (value, { color = "black", fontWeight = "normal", maxChars = 16 } = {}) => {
    const text = `${value ?? ""}`;
    const isClipped = text.length > maxChars;
    const label = isClipped ? `${text.slice(0, maxChars - 1)}…` : text;
    return `<span class="asset-chart-axis-label" style="color: ${color}; font-weight: ${fontWeight}">${escapeHtml(label)}</span>`;
};

export const getEndOfLifeGroupedRows = (chartResponse, groupBy) => {
    const rows = chartResponse?.end_of_life_chart;
    if (!Array.isArray(rows) || !rows.length) return [];
    if (Array.isArray(rows[0]?.data)) return [];
    return isGroupedCostChart(groupBy)
        ? rows.map(row => ({
              ...row,
              y: row.value || 0,
              assetCount: row.valueAssetCount ?? row.assetCount ?? 0,
              totalAssetCount: row.assetCount ?? 0
          }))
        : rows;
};
